import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgentControlProvider } from "src/components/llm/embedded-chat/agent-control-context";

type HighlightedConditionContextType = {
  highlightedConditionId: string | null;
  /**
   * Highlight a condition in the audience builder (e.g. when the user clicks a
   * focused-filter pill in chat history). Auto-clears after a short delay so
   * the highlight doesn't linger. `null` when no provider is mounted (e.g.
   * chat history viewed outside the audience builder) — callers should skip
   * rendering click-to-highlight affordances in that case.
   */
  setHighlightedConditionId: ((id: string | null) => void) | null;
};

const HIGHLIGHT_DURATION_MS = 3_000;

const HighlightedConditionContext =
  createContext<HighlightedConditionContextType>({
    highlightedConditionId: null,
    setHighlightedConditionId: null,
  });
HighlightedConditionContext.displayName = "HighlightedConditionContext";

export const useHighlightedConditionContext = () =>
  useContext(HighlightedConditionContext);

export const HighlightedConditionProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [highlightedConditionId, setId] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const rafRef = useRef<number | null>(null);

  const setHighlightedConditionId = useCallback((id: string | null) => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // Clear first so re-clicking the same condition restarts the highlight.
    setId(null);
    if (id !== null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setId(id);
        timeoutRef.current = setTimeout(() => {
          setId(null);
          timeoutRef.current = null;
        }, HIGHLIGHT_DURATION_MS);
      });
    }
  }, []);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const value = useMemo(
    () => ({ highlightedConditionId, setHighlightedConditionId }),
    [highlightedConditionId, setHighlightedConditionId],
  );

  return (
    <HighlightedConditionContext.Provider value={value}>
      {children}
    </HighlightedConditionContext.Provider>
  );
};

/**
 * Combines AgentControlProvider + HighlightedConditionProvider so the audience
 * explore page only adds one wrapper level (keeps the diff small).
 *
 * `useAncestorProviders` skips mounting both providers and renders children
 * directly — for surfaces (the audience Definition tab with the page-level
 * agent sidebar) where an ancestor already mounts them and a nested pair
 * would split the listener registries between the builder and the sidebar.
 */
export const AudienceAgentProviders: FC<
  PropsWithChildren<{
    agentAvailable: boolean;
    useAncestorProviders?: boolean;
  }>
> = ({ agentAvailable, useAncestorProviders = false, children }) =>
  useAncestorProviders ? (
    <>{children}</>
  ) : (
    <AgentControlProvider agentAvailable={agentAvailable}>
      <HighlightedConditionProvider>{children}</HighlightedConditionProvider>
    </AgentControlProvider>
  );
